import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Users, Settings, Bell, CalendarDays, MapPin, Plus, Trash2, KeyRound, Shield, UserPlus, Grid3X3, Clock, ToggleLeft, Printer, Copy, CheckCircle2, XCircle } from 'lucide-react';

const roleColors = { manager: 'bg-gold/20 text-gold', director: 'bg-purple-500/20 text-purple-400', chef: 'bg-amber-500/20 text-amber-400', bartender: 'bg-blue-500/20 text-blue-400', shisha_expert: 'bg-pink-500/20 text-pink-400', waiter: 'bg-emerald-500/20 text-emerald-400' };
const ROLES = [
  { value: 'director', label: 'Director' }, { value: 'manager', label: 'Manager' },
  { value: 'chef', label: 'Chef' }, { value: 'bartender', label: 'Bartender' },
  { value: 'shisha_expert', label: 'Shisha Expert' }, { value: 'waiter', label: 'Waiter/Service' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [shiftSettings, setShiftSettings] = useState({});
  const [leaveSettings, setLeaveSettings] = useState({});
  const [notifSettings, setNotifSettings] = useState({});
  const [tables, setTables] = useState([]);
  const [modules, setModules] = useState({});
  const [resetRequests, setResetRequests] = useState([]);
  const [approvedPwdDialog, setApprovedPwdDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [resetDialog, setResetDialog] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'waiter', password: '', phone: '' });
  const [editForm, setEditForm] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const isDirector = user?.role === 'director';

  const fetchUsers = useCallback(async () => { try { const { data } = await api.get('/settings/users'); setUsers(data); } catch (err) { console.error('fetchUsers failed:', err); } }, []);
  const fetchApp = useCallback(async () => { try { const { data } = await api.get('/settings/app'); setAppSettings(data); } catch (err) { console.error('fetchApp failed:', err); } }, []);
  const fetchShifts = useCallback(async () => { try { const { data } = await api.get('/settings/shifts'); setShiftSettings(data); } catch (err) { console.error('fetchShifts failed:', err); } }, []);
  const fetchLeave = useCallback(async () => { try { const { data } = await api.get('/settings/leave'); setLeaveSettings(data); } catch (err) { console.error('fetchLeave failed:', err); } }, []);
  const fetchNotifs = useCallback(async () => { try { const { data } = await api.get('/settings/notifications'); setNotifSettings(data); } catch (err) { console.error('fetchNotifs failed:', err); } }, []);
  const fetchTables = useCallback(async () => { try { const { data } = await api.get('/settings/tables'); setTables(data.tables || []); } catch (err) { console.error('fetchTables failed:', err); } }, []);
  const fetchModules = useCallback(async () => { try { const { data } = await api.get('/settings/modules'); setModules(data); } catch (err) { console.error('fetchModules failed:', err); } }, []);
  const fetchResetRequests = useCallback(async () => { try { const { data } = await api.get('/settings/password-reset-requests?status=pending'); setResetRequests(data || []); } catch (err) { console.error('fetchResetRequests failed:', err); } }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { if (tab === 'app') fetchApp(); if (tab === 'shifts') fetchShifts(); if (tab === 'leave') fetchLeave(); if (tab === 'notifications') fetchNotifs(); if (tab === 'tables') fetchTables(); if (tab === 'modules') fetchModules(); if (tab === 'password-requests') fetchResetRequests(); }, [tab, fetchApp, fetchShifts, fetchLeave, fetchNotifs, fetchTables, fetchModules, fetchResetRequests]);

  const createUser = async () => {
    setSaving(true);
    try { await api.post('/settings/users', newUser); toast.success('User created'); setCreateDialog(false); setNewUser({ email: '', name: '', role: 'waiter', password: '', phone: '' }); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const updateUser = async () => {
    if (!editDialog) return;
    setSaving(true);
    try { await api.put(`/settings/users/${editDialog.id}`, editForm); toast.success('User updated'); setEditDialog(null); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (!resetDialog || !newPassword) return;
    try { await api.put(`/settings/users/${resetDialog.id}/reset-password`, { new_password: newPassword }); toast.success('Password reset'); setResetDialog(null); setNewPassword(''); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?`)) return;
    try { await api.delete(`/settings/users/${u.id}`); toast.success('User deleted'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveAppSettings = async () => {
    try { await api.put('/settings/app', appSettings); toast.success('App settings saved'); } catch (err) { toast.error('Failed'); }
  };

  const saveShiftSettings = async () => {
    try { await api.put('/settings/shifts', shiftSettings); toast.success('Shift settings saved'); } catch (err) { toast.error('Failed'); }
  };

  const saveLeaveSettings = async () => {
    try { await api.put('/settings/leave', leaveSettings); toast.success('Leave settings saved'); } catch (err) { toast.error('Failed'); }
  };

  const toggleNotif = async (key) => {
    const updated = { ...notifSettings, [key]: !notifSettings[key] };
    setNotifSettings(updated);
    try { await api.put('/settings/notifications', { [key]: updated[key] }); } catch (err) { console.error('toggleNotif failed:', err); toast.error('Failed'); }
  };

  const addTable = async () => {
    try { await api.post('/settings/tables', { capacity: 4 }); toast.success('Table added'); fetchTables(); } catch (err) { toast.error('Failed'); }
  };

  const deleteTable = async (id) => {
    try { await api.delete(`/settings/tables/${id}`); toast.success('Table deleted'); fetchTables(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleModule = async (key) => {
    if (!isDirector) { toast.error('Only directors can toggle modules'); return; }
    const updated = { ...modules, [key]: !modules[key] };
    setModules(updated);
    try {
      await api.put('/settings/modules', { [key]: updated[key] });
      toast.success(`${key.replace(/_/g, ' ')} ${updated[key] ? 'enabled' : 'disabled'}`);
      window.dispatchEvent(new Event('modules-updated'));
    } catch (err) { console.error('toggleModule failed:', err); toast.error('Failed'); setModules(modules); }
  };

  const tabs = [
    { key: 'users', label: 'Users', icon: Users },
    { key: 'password-requests', label: 'Password Requests', icon: KeyRound },
    { key: 'modules', label: 'Modules', icon: ToggleLeft },
    { key: 'app', label: 'Venue', icon: Settings },
    { key: 'shifts', label: 'Shifts & Clock', icon: Clock },
    { key: 'leave', label: 'Leave', icon: CalendarDays },
    { key: 'notifications', label: 'Alerts', icon: Bell },
    { key: 'tables', label: 'Tables', icon: Grid3X3 },
  ];

  const approveReset = async (reqId) => {
    try {
      const { data } = await api.post(`/settings/password-reset-requests/${reqId}/approve`);
      setApprovedPwdDialog(data);
      fetchResetRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };
  const rejectReset = async (reqId) => {
    if (!window.confirm('Reject this password reset request?')) return;
    try {
      await api.post(`/settings/password-reset-requests/${reqId}/reject`);
      toast.success('Request rejected');
      fetchResetRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
  };
  const copyTempPwd = () => {
    if (approvedPwdDialog?.temporary_password) {
      navigator.clipboard.writeText(approvedPwdDialog.temporary_password);
      toast.success('Copied to clipboard');
    }
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex flex-wrap gap-1.5 mb-6 border-b border-white/5 pb-3">
        {tabs.map(t => (
          <Button key={t.key} size="sm" variant={tab === t.key ? 'default' : 'ghost'} onClick={() => setTab(t.key)}
            className={`h-8 rounded-sm font-sans text-xs ${tab === t.key ? 'bg-gold/10 text-gold border border-gold/20' : 'text-zinc-500 hover:text-zinc-300'}`}
            data-testid={`settings-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5 mr-1" /> {t.label}
          </Button>
        ))}
      </div>

      {/* USERS TAB */}
      {tab === 'users' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm font-sans text-zinc-400">{users.length} staff accounts</p>
            <Button size="sm" onClick={() => setCreateDialog(true)} className="h-8 bg-gold text-black font-bold rounded-sm font-sans text-xs" data-testid="create-user-btn"><UserPlus className="w-3 h-3 mr-1" /> New User</Button>
          </div>
          <div className="space-y-2">
            {users.map(u => (
              <div key={u.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm flex items-center justify-between" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gold/10 flex items-center justify-center text-gold font-sans font-bold text-sm">{u.name?.[0]?.toUpperCase() || '?'}</div>
                  <div>
                    <p className="text-white font-sans text-sm">{u.name} {u.is_active === false && <Badge className="bg-red-500/20 text-red-400 border-0 text-[10px] ml-1">Inactive</Badge>}</p>
                    <p className="text-zinc-500 font-sans text-xs">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${roleColors[u.role] || 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-xs`}>{u.role}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => { setEditDialog(u); setEditForm({ name: u.name, email: u.email, role: u.role, is_active: u.is_active !== false, phone: u.phone || '' }); }} className="h-7 text-zinc-400 hover:text-white font-sans text-xs" data-testid={`edit-user-${u.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setResetDialog(u); setNewPassword(''); }} className="h-7 text-zinc-400 hover:text-amber-400 font-sans text-xs" data-testid={`reset-pwd-${u.id}`}><KeyRound className="w-3 h-3" /></Button>
                  {isDirector && u.email !== 'admin@majnu.com.au' && (
                    <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} className="h-7 text-zinc-400 hover:text-red-400" data-testid={`delete-user-${u.id}`}><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PASSWORD REQUESTS TAB */}
      {tab === 'password-requests' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-sm font-sans text-zinc-300 font-medium">{resetRequests.length} pending {resetRequests.length === 1 ? 'request' : 'requests'}</p>
              <p className="text-xs font-sans text-zinc-500 mt-0.5">Staff who clicked "Forgot password?" on the login page. Approve to generate a one-time temporary password.</p>
            </div>
            <Button size="sm" onClick={fetchResetRequests} variant="ghost" className="h-8 text-zinc-400 hover:text-white font-sans text-xs" data-testid="refresh-reset-requests">Refresh</Button>
          </div>
          {resetRequests.length === 0 ? (
            <div className="p-8 border border-white/5 rounded-sm text-center">
              <KeyRound className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm font-sans text-zinc-500">No pending password reset requests.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {resetRequests.map(r => (
                <div key={r.id} className="p-4 bg-white/[0.03] border border-amber-500/20 rounded-sm flex items-center justify-between" data-testid={`reset-request-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-400 font-sans font-bold text-sm">{r.name?.[0]?.toUpperCase() || '?'}</div>
                    <div>
                      <p className="text-white font-sans text-sm">{r.name || r.email}</p>
                      <p className="text-zinc-500 font-sans text-xs">{r.email} · <Badge className={`${roleColors[r.role] || 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-[10px] ml-1`}>{r.role}</Badge></p>
                      <p className="text-zinc-600 font-sans text-[10px] mt-0.5">Requested: {new Date(r.requested_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => approveReset(r.id)} className="h-8 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30 font-sans text-xs" data-testid={`approve-reset-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" onClick={() => rejectReset(r.id)} variant="ghost" className="h-8 text-zinc-400 hover:text-red-400 font-sans text-xs" data-testid={`reject-reset-${r.id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VENUE TAB */}
      {tab === 'app' && (
        <div className="max-w-lg space-y-4">
          {[{k:'venue_name',l:'Venue Name'},{k:'venue_address',l:'Address'},{k:'phone',l:'Phone'},{k:'email',l:'Email'},{k:'website',l:'Website'},{k:'timezone',l:'Timezone'}].map(f => (
            <div key={f.k} className="space-y-1">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">{f.l}</Label>
              <Input value={appSettings[f.k] || ''} onChange={e => setAppSettings(p => ({...p, [f.k]: e.target.value}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid={`app-${f.k}`} />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Currency</Label><Input value={appSettings.currency || ''} onChange={e => setAppSettings(p => ({...p, currency: e.target.value}))} className="bg-transparent border border-white/10 text-white font-sans" /></div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Symbol</Label><Input value={appSettings.currency_symbol || ''} onChange={e => setAppSettings(p => ({...p, currency_symbol: e.target.value}))} className="bg-transparent border border-white/10 text-white font-sans" /></div>
          </div>
          <Button onClick={saveAppSettings} className="bg-gold text-black font-bold rounded-sm font-sans" data-testid="save-app-settings">Save Venue Settings</Button>
        </div>
      )}

      {/* SHIFTS & CLOCK TAB */}
      {tab === 'shifts' && (
        <div className="max-w-lg space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Grace Period (min)</Label><Input type="number" value={shiftSettings.grace_period_minutes || ''} onChange={e => setShiftSettings(p => ({...p, grace_period_minutes: parseInt(e.target.value) || 0}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="shift-grace" /></div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">OT Threshold (hrs)</Label><Input type="number" value={shiftSettings.overtime_threshold_hours || ''} onChange={e => setShiftSettings(p => ({...p, overtime_threshold_hours: parseInt(e.target.value) || 0}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="shift-ot" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">OT Multiplier</Label><Input type="number" step="0.1" value={shiftSettings.overtime_multiplier || ''} onChange={e => setShiftSettings(p => ({...p, overtime_multiplier: parseFloat(e.target.value) || 1.5}))} className="bg-transparent border border-white/10 text-white font-sans" /></div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Default Break (min)</Label><Input type="number" value={shiftSettings.default_break_minutes || ''} onChange={e => setShiftSettings(p => ({...p, default_break_minutes: parseInt(e.target.value) || 0}))} className="bg-transparent border border-white/10 text-white font-sans" /></div>
          </div>
          <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-sans font-bold pt-2">Geofence</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs text-zinc-400 font-sans">Latitude</Label><Input type="number" step="0.0001" value={shiftSettings.geofence_lat || ''} onChange={e => setShiftSettings(p => ({...p, geofence_lat: parseFloat(e.target.value)}))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="geo-lat" /></div>
            <div className="space-y-1"><Label className="text-xs text-zinc-400 font-sans">Longitude</Label><Input type="number" step="0.0001" value={shiftSettings.geofence_lng || ''} onChange={e => setShiftSettings(p => ({...p, geofence_lng: parseFloat(e.target.value)}))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="geo-lng" /></div>
            <div className="space-y-1"><Label className="text-xs text-zinc-400 font-sans">Radius (m)</Label><Input type="number" value={shiftSettings.geofence_radius_meters || ''} onChange={e => setShiftSettings(p => ({...p, geofence_radius_meters: parseInt(e.target.value) || 200}))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="geo-radius" /></div>
          </div>
          <Button onClick={saveShiftSettings} className="bg-gold text-black font-bold rounded-sm font-sans" data-testid="save-shift-settings">Save Shift Settings</Button>
        </div>
      )}

      {/* LEAVE TAB */}
      {tab === 'leave' && (
        <div className="max-w-lg space-y-6">
          {[{k:'full_time',l:'Full Time'},{k:'part_time',l:'Part Time'},{k:'casual',l:'Casual'}].map(emp => (
            <div key={emp.k}>
              <p className="text-sm font-sans text-zinc-300 font-medium mb-2">{emp.l}</p>
              <div className="grid grid-cols-3 gap-3">
                {['annual','sick','personal'].map(type => (
                  <div key={type} className="space-y-1">
                    <Label className="text-xs text-zinc-400 font-sans capitalize">{type}</Label>
                    <Input type="number" value={leaveSettings[emp.k]?.[type] ?? ''} onChange={e => setLeaveSettings(p => ({...p, [emp.k]: {...(p[emp.k]||{}), [type]: parseInt(e.target.value) || 0}}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid={`leave-${emp.k}-${type}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <Button onClick={saveLeaveSettings} className="bg-gold text-black font-bold rounded-sm font-sans" data-testid="save-leave-settings">Save Leave Settings</Button>
        </div>
      )}

      {/* NOTIFICATIONS TAB */}
      {tab === 'notifications' && (
        <div className="max-w-lg space-y-3">
          {[
            {k:'late_arrival_alert',l:'Late Arrival Alert',d:'Notify managers when staff clocks in late'},
            {k:'geofence_alert',l:'Geofence Alert',d:'Alert when clock-in is outside venue'},
            {k:'overtime_alert',l:'Overtime Alert',d:'Alert when staff exceeds overtime threshold'},
            {k:'roster_publish_push',l:'Roster Published',d:'Push to all staff when roster is published'},
            {k:'leave_request_push',l:'Leave Request',d:'Notify managers of new leave requests'},
            {k:'shift_swap_push',l:'Shift Swap',d:'Notify on shift swap requests'},
            {k:'low_stock_alert',l:'Low Stock Alert',d:'Alert when inventory is low'},
            {k:'order_delay_alert',l:'Order Delay Alert',d:'Alert when orders take too long'},
          ].map(n => (
            <div key={n.k} className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`notif-${n.k}`}>
              <div><p className="text-white font-sans text-sm">{n.l}</p><p className="text-zinc-500 font-sans text-xs">{n.d}</p></div>
              <Switch checked={notifSettings[n.k] !== false} onCheckedChange={() => toggleNotif(n.k)} />
            </div>
          ))}
        </div>
      )}

      {/* MODULES TAB */}
      {tab === 'modules' && (
        <div className="max-w-2xl">
          <div className="mb-4 p-3 bg-gold/5 border border-gold/20 rounded-sm">
            <p className="text-sm font-sans text-gold">Toggle features on/off across the entire venue.</p>
            <p className="text-xs font-sans text-zinc-400 mt-1">{isDirector ? 'Disabled modules are hidden from all staff sidebars instantly.' : 'Only Directors can change module settings.'}</p>
          </div>
          {[
            { title: 'Operations', items: [['orders','Live Orders'],['menu','Menu Management'],['tables','Table Management']] },
            { title: 'Stock', items: [['inventory','Inventory'],['purchase_orders','Purchase Orders'],['suppliers','Suppliers'],['till','Till']] },
            { title: 'Team', items: [['staff','Staff'],['performance','Performance'],['feedback','Feedback'],['damages','Damage Claims']] },
            { title: 'Insights', items: [['analytics','Analytics'],['audit','Audit Log'],['reports','Reports'],['locations','Locations'],['hard_delete','Hard Delete']] },
            { title: 'Staff Management', items: [['staff_management','Staff Hub'],['roster','Roster'],['clock','Clock In/Out'],['leave','Leave & Swaps'],['timesheets','Timesheets']] },
            { title: 'Other', items: [['quick_links','Quick Links'],['push_notifications','Web Push Notifications']] },
          ].map(group => (
            <div key={group.title} className="mb-5">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-2">{group.title}</p>
              <div className="space-y-1.5">
                {group.items.map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`module-${k}`}>
                    <p className="text-white font-sans text-sm">{label}</p>
                    <Switch checked={modules[k] !== false} onCheckedChange={() => toggleModule(k)} disabled={!isDirector} data-testid={`module-toggle-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TABLES TAB */}
      {tab === 'tables' && (
        <div>
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <p className="text-sm font-sans text-zinc-400">{tables.length} tables</p>
            <div className="flex gap-2">
              {tables.length > 0 && (
                <Button size="sm" onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/settings/tables/qr-pdf?base_url=${encodeURIComponent(window.location.origin)}`, '_blank'); }} className="h-8 bg-white/5 text-zinc-300 border border-white/10 rounded-sm font-sans text-xs hover:bg-white/10" data-testid="download-qr-pdf-btn">
                  <Printer className="w-3 h-3 mr-1" /> Print QR Codes (PDF)
                </Button>
              )}
              <Button size="sm" onClick={addTable} className="h-8 bg-gold text-black font-bold rounded-sm font-sans text-xs" data-testid="add-table-btn"><Plus className="w-3 h-3 mr-1" /> Add Table</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {tables.map(t => (
              <div key={t.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm text-center" data-testid={`table-${t.id}`}>
                <p className="text-2xl font-sans text-gold font-bold">{t.number}</p>
                <p className="text-xs text-zinc-500 font-sans">{t.qr_code}</p>
                <p className="text-xs text-zinc-400 font-sans mt-1">Capacity: {t.capacity}</p>
                {isDirector && (
                  <Button size="sm" variant="ghost" onClick={() => deleteTable(t.id)} className="h-6 text-red-400 hover:text-red-300 mt-2 text-xs"><Trash2 className="w-3 h-3" /></Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CREATE USER DIALOG */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-white">Create Staff Account</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Name</Label><Input value={newUser.name} onChange={e => setNewUser(p => ({...p, name: e.target.value}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="new-user-name" /></div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Email</Label><Input value={newUser.email} onChange={e => setNewUser(p => ({...p, email: e.target.value}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="new-user-email" /></div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Phone (for SMS reset)</Label><Input value={newUser.phone} onChange={e => setNewUser(p => ({...p, phone: e.target.value}))} placeholder="+61 4XX XXX XXX" className="bg-transparent border border-white/10 text-white font-sans" data-testid="new-user-phone" /></div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Role</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser(p => ({...p, role: v}))}>
                <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-secondary border border-white/10">{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Password</Label><Input value={newUser.password} onChange={e => setNewUser(p => ({...p, password: e.target.value}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="new-user-password" /></div>
            <Button onClick={createUser} disabled={saving} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="submit-create-user">{saving ? 'Creating...' : 'Create User'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT USER DIALOG */}
      <Dialog open={!!editDialog} onOpenChange={v => { if (!v) setEditDialog(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-white">Edit {editDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Name</Label><Input value={editForm.name || ''} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="edit-user-name" /></div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Email</Label><Input value={editForm.email || ''} onChange={e => setEditForm(p => ({...p, email: e.target.value}))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="edit-user-email" /></div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Role</Label>
              <Select value={editForm.role || ''} onValueChange={v => setEditForm(p => ({...p, role: v}))}>
                <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-secondary border border-white/10">{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-400 font-sans">Active</Label>
              <Switch checked={editForm.is_active !== false} onCheckedChange={v => setEditForm(p => ({...p, is_active: v}))} data-testid="edit-user-active" />
            </div>
            <Button onClick={updateUser} disabled={saving} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="submit-edit-user">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD DIALOG */}
      <Dialog open={!!resetDialog} onOpenChange={v => { if (!v) setResetDialog(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-white">Reset Password — {resetDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">New Password</Label><Input value={newPassword} onChange={e => setNewPassword(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Enter new password" data-testid="reset-password-input" /></div>
            <Button onClick={resetPassword} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="submit-reset-password">Reset Password</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* APPROVED TEMP PASSWORD DIALOG (shown once after approving a reset request) */}
      <Dialog open={!!approvedPwdDialog} onOpenChange={v => { if (!v) setApprovedPwdDialog(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-emerald-500/30 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Password Reset Approved
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs font-sans text-zinc-400">
              <span className="text-white font-medium">{approvedPwdDialog?.user_name}</span> ({approvedPwdDialog?.user_email}) has a new temporary password. Share it verbally — this is shown only once.
            </p>
            <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-sm flex items-center justify-between">
              <code className="text-emerald-300 font-mono text-lg tracking-wider select-all" data-testid="temporary-password-display">{approvedPwdDialog?.temporary_password}</code>
              <Button size="sm" onClick={copyTempPwd} variant="ghost" className="h-8 text-emerald-400 hover:bg-emerald-500/10 font-sans text-xs" data-testid="copy-temp-password">
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <p className="text-[10px] font-sans text-zinc-500 italic">Instruct the user to log in and change their password immediately via the "Change Password" option in the sidebar.</p>
            <Button onClick={() => setApprovedPwdDialog(null)} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="close-temp-password-dialog">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
